<template>
  <!-- Hero Section -->
  <div
    class="relative bg-no-repeat bg-center bg-cover flex flex-col-reverse pb-4 sm:pb-6 lg:pb-10 h-32 sm:h-40 lg:h-60"
    :style="{
      backgroundImage: `url(${cover})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }">
    <div class="absolute inset-0 bg-[#4664b2] bg-opacity-40"></div>
    <div class="absolute inset-0 flex flex-col items-center justify-center text-white px-4">
      <h1 class="text-xl sm:text-2xl lg:text-3xl font-bold text-center mb-2 lg:mb-4">
        {{ title }}
      </h1>
      <!-- Breadcrumb -->
      <nav class="text-xs sm:text-sm lg:text-base font-medium" aria-label="Breadcrumb">
        <ol class="flex items-center space-x-2">
          <li>
            <a 
              :href="`/`" 
              class="hover:underline transition-colors"
              :aria-label="$t('home')">
              {{ $t("home") }}
            </a>
          </li>
          <li class="flex items-center">
            <span class="mx-2">{{ currentLanguage === 'ar' ? '/' : '\\' }}</span>
            <a 
              :href="parentUrl" 
              class="hover:underline transition-colors"
              :aria-label="title">
              {{ title }}
            </a>
          </li>
        </ol>
      </nav>
    </div>
  </div>

  <!-- Main Content -->
  <div class="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-16 py-4 sm:py-6">
    <!-- Mobile Filter Button -->
    <div class="block lg:hidden mb-4">
      <button
        @click="showFilter = true"
        class="w-full flex items-center justify-center space-x-2 bg-[#4664b2] text-white py-3 px-4 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200"
        :aria-label="$t('open_filters')">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707v4.586a1 1 0 01-.293.707L9 19.414V13.414a1 1 0 00-.293-.707L2.293 6.293A1 1 0 012 5.586V4z"/>
        </svg>
        <span>{{ $t("search_and_filter") }}</span>
      </button>
    </div>

    <!-- Desktop Layout -->
    <div class="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8 mb-6">
      <!-- Sidebar - Desktop Only -->
      <div class="hidden lg:block lg:col-span-1">
        <div class="sticky top-4 space-y-6">
          <!-- Search Form -->
          <div class="bg-[#C9F2C7] rounded-xl p-4 shadow-sm">
            <h2 class="text-lg font-bold text-[#4664b2] mb-4">
              {{ $t("search") }}
            </h2>
            <form @submit.prevent="submitForm()" class="space-y-4">
              <!-- Destination Select -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  {{ $t("destination") }}
                </label>
                <select
                  v-model="form.destination"
                  class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors">
                  <option value="">{{ $t("choose_your_destination") }}</option>
                  <option
                    v-for="(item, index) in processedDestination"
                    :key="index"
                    :value="item.id">
                    {{ getLocalizedName(item) }}
                  </option>
                </select>
              </div>
              
              <!-- Submit Button -->
              <button
                type="submit"
                class="w-full bg-gradient-to-r from-[#2FB784] to-[#4664b2] hover:from-[#4664b2] hover:to-[#2FB784] text-white py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                <span>{{ $t("search") }}</span>
              </button>
            </form>
          </div>

          <!-- Map -->
          <div class="bg-white rounded-xl shadow-sm overflow-hidden">
            <DynamicMapPopUp :elements="elements" :icon="icon" />
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="lg:col-span-3">
        <!-- Search Bar -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div class="flex-1">
            <div class="relative">
              <input
                type="text"
                :placeholder="$t('searchByName')"
                v-model="searchTerm"
                @keyup.enter="searchElement()"
                class="w-full pl-4 pr-12 py-3 text-gray-700 placeholder-gray-400 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors">
              <button
                @click="searchElement()"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#194032] hover:text-[#194032] transition-colors"
                :aria-label="$t('search')">
                <svg class="w-5 h-5" fill="none" stroke="#2C5BA1" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </button>
            </div>
          </div>

        </div>

        <!-- Results Grid -->
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 mb-8">
          <div v-for="(hotel, index) in elements" :key="hotel.id || index">
            <AnnuaireCard
              :element="hotel"
              :expanded="expandedElement === index"
              @toggleExpand="toggleExpand(index)" />
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="elements.length === 0" class="text-center py-12">
          <div class="max-w-md mx-auto">
            <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <h3 class="text-lg font-medium text-gray-900 mb-2">{{ $t('no_results') }}</h3>
            <p class="text-gray-600">{{ $t('try_different_search') }}</p>
          </div>
        </div>

        <!-- Responsive Pagination -->
        <nav v-if="links.length > 0" class="mt-8" aria-label="Pagination">
          <!-- Mobile Pagination -->
          <div class="block sm:hidden">
            <div class="flex items-center justify-between mb-4">
              <button
                v-if="hasPreviousPage"
                @click="emitPageChange(previousPage)"
                class="flex items-center justify-center w-12 h-12 rounded-full bg-white border-2 border-[#4664b2] text-[#4664b2] hover:bg-[#4664b2] hover:text-white transition-all duration-200 shadow-sm">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                </svg>
              </button>

              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-600">{{ $t('page') }}</span>
                <span class="px-3 py-2 bg-[#4664b2] text-white rounded-lg font-medium">{{ page }}</span>
                <span class="text-sm text-gray-600">{{ $t('of') }}</span>
                <span class="text-sm text-gray-600">{{ lastPage }}</span>
              </div>

              <button
                v-if="hasNextPage"
                @click="emitPageChange(nextPage)"
                class="flex items-center justify-center w-12 h-12 rounded-full bg-white border-2 border-[#4664b2] text-[#4664b2] hover:bg-[#4664b2] hover:text-white transition-all duration-200 shadow-sm">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            </div>

            <!-- Page Jump Input -->
            <div class="flex items-center justify-center space-x-3">
              <span class="text-sm text-gray-600">{{ $t('go_to_page') }}:</span>
              <input
                v-model.number="jumpToPage"
                @keyup.enter="jumpToPageHandler"
                type="number"
                :min="1"
                :max="lastPage"
                class="w-16 h-10 text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
              <button
                @click="jumpToPageHandler"
                class="px-4 py-2 bg-[#4664b2] text-white rounded-lg text-sm font-medium hover:bg-[#4664b2]/90 transition-colors">
                {{ $t('go') }}
              </button>
            </div>
          </div>

          <!-- Desktop Pagination -->
          <div class="hidden sm:flex items-center justify-center space-x-1">
            <button
              v-if="hasPreviousPage"
              @click="emitPageChange(previousPage)"
              class="px-3 py-2 rounded-lg text-sm font-medium transition-colors bg-white text-gray-700 hover:bg-gray-50 border border-gray-300">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
            </button>

            <template v-for="(link, index) in visibleLinks" :key="index">
              <button
                v-if="link.label !== 'pagination.previous' && link.label !== 'pagination.next'"
                @click="emitPageChange(link)"
                :disabled="!link?.url"
                :class="[
                  'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  link.active
                    ? 'bg-[#4664b2] text-white shadow-md'
                    : link.url
                    ? 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                ]">
                {{ link?.label }}
              </button>
            </template>

            <button
              v-if="hasNextPage"
              @click="emitPageChange(nextPage)"
              class="px-3 py-2 rounded-lg text-sm font-medium transition-colors bg-white text-gray-700 hover:bg-gray-50 border border-gray-300">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </nav>
      </div>
    </div>

    <!-- Activities Section -->
    <section class="mb-8 sm:mb-12">
      <h2 class="text-xl sm:text-2xl font-bold mb-6 relative inline-block pb-2 bg-gradient-to-r from-[#15A5DF] to-[#15A5DF] bg-[length:60%_2.5px] bg-left-bottom bg-no-repeat">
        {{ $t("activites") }}
      </h2>
      <ActivityList :activities="activitiesList" />
    </section>

   

    <!-- Banner Section -->
    <section v-if="imageListBanner1.length !== 0" class="mb-8 sm:mb-12" data-aos="fade-up">
      <BannerPub :images="imageListBanner1" />
    </section>
  </div>

  <!-- Mobile Filter Modal -->
  <transition
    enter-active-class="transition-opacity duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-300"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0">
    <div
      v-if="showFilter"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50"
      @click.self="showFilter = false">
      
      <transition
        enter-active-class="transition-transform duration-300"
        enter-from-class="translate-y-full sm:scale-95"
        enter-to-class="translate-y-0 sm:scale-100"
        leave-active-class="transition-transform duration-300"
        leave-from-class="translate-y-0 sm:scale-100"
        leave-to-class="translate-y-full sm:scale-95">
        <div class="bg-white w-full sm:w-96 sm:max-h-[80vh] sm:rounded-xl shadow-xl overflow-hidden">
          <!-- Header -->
          <div class="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-900">{{ $t("search_and_filter") }}</h3>
            <button
              @click="showFilter = false"
              class="p-2 hover:bg-gray-100 rounded-full transition-colors"
              :aria-label="$t('close')">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="p-4 space-y-6 max-h-[60vh] sm:max-h-none overflow-y-auto">
            <!-- Search Form -->
            <div class="bg-[#C9F2C7] rounded-lg p-4">
              <h4 class="text-base font-semibold text-[#4664b2] mb-4">{{ $t("search") }}</h4>
              <form @submit.prevent="submitForm(); showFilter = false" class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    {{ $t("destination") }}
                  </label>
                  <select
                    v-model="form.destination"
                    class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="">{{ $t("choose_your_destination") }}</option>
                    <option
                      v-for="(item, index) in processedDestination"
                      :key="index"
                      :value="item.id">
                      {{ getLocalizedName(item) }}
                    </option>
                  </select>
                </div>
                
                <button
                  type="submit"
                  class="w-full bg-[#4664b2] text-white py-3 px-4 rounded-lg font-medium flex items-center justify-center space-x-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                  <span>{{ $t("search") }}</span>
                </button>
              </form>
            </div>

            <!-- Map Preview -->
            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                @click="showModalMainMap = true; showFilter = false"
                class="w-full p-0 border-0 bg-transparent">
                <img
                  src="/mapMarkers/mappp.png"
                  alt="Map GabongoTravel"
                  class="w-full h-32 object-cover hover:opacity-90 transition-opacity">
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import BannerPub from "./BannerPub.vue";
  
import ActivityList from "./ActivityList.vue";
import AnnuaireCard from "./AnnuaireCard.vue";
import DynamicMapPopUp from "./DynamicMapPopUp";
import { mapActions } from "vuex";

export default {
  name: 'ImprovedListingPage',
  components: {
    BannerPub,
  
    ActivityList,
    AnnuaireCard,
    DynamicMapPopUp,
  },
  props: {
    title: String,
    cover: String,
    url: String,
    typeElement: String,
    icon: String,
  },
  data() {
    return {
      currentLanguage: this.$i18n.locale,
      showFilter: false,
      searchTerm: "",
      expandedElement: null,
      imageListBanner1: [],
      imageListBanner2: [],
      imageListBanner3: [],
      showModalMainMap: false,
      activitiesList: [],
      articles: [],
      destinations: [],
      elements: [],
      total: 0,
      elementCurrentPage: 15,
      page: 1,
      lastPage: 0,
      links: [],
      jumpToPage: null,
      form: {
        destination: "",
      },
    };
  },
  computed: {
    processedDestination() {
      return this.destinations;
    },
    
    parentUrl() {
      return this.url || '#';
    },

    // Pagination helpers
    hasPreviousPage() {
      return this.links.length > 0 && this.links[0]?.label === 'pagination.previous' && this.links[0]?.url;
    },

    hasNextPage() {
      return this.links.length > 0 && this.links[this.links.length - 1]?.label === 'pagination.next' && this.links[this.links.length - 1]?.url;
    },

    previousPage() {
      return this.links[0];
    },

    nextPage() {
      return this.links[this.links.length - 1];
    },

    visibleLinks() {
      return this.links.filter(link => 
        link.label !== 'pagination.previous' && 
        link.label !== 'pagination.next'
      );
    }
  },
  watch: {
    "$i18n.locale"(newLocale) {
      this.currentLanguage = newLocale;
    },
  },
  methods: {
    ...mapActions([
      "fetchBanners",
      "fetchArticles",
      "fetchActivity",
      "fetchDestinations",
      "fetchHotelsByPage",
      "fetchRestaurantsByPage",
    ]),

    // Get localized name for destinations
    getLocalizedName(item) {
      const langSuffix = this.currentLanguage === 'fr' ? '' : `_${this.currentLanguage}`;
      const key = `name${langSuffix}`;
      return item[key] || item.name || '';
    },

    toggleExpand(index) {
      this.expandedElement = this.expandedElement === index ? null : index;
    },

    // Jump to specific page
    jumpToPageHandler() {
      if (this.jumpToPage && this.jumpToPage >= 1 && this.jumpToPage <= this.lastPage) {
        const targetLink = this.links.find(link => parseInt(link.label) === this.jumpToPage);
        if (targetLink && targetLink.url) {
          this.emitPageChange(targetLink);
          this.jumpToPage = null;
        }
      }
    },

    async fetchElementsPaging(url) {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      return await response.json();
    },

    async emitPageChange(link) {
      if (link.url) {
        try {
          let elements = await this.fetchElementsPaging(link.url);
          if (this.$props.typeElement === "hotels") {
            elements = elements.hotels;
          }
          if (this.$props.typeElement === "restaurants") {
            elements = elements.restaurants;
          }
          this.elements = elements.data;
          this.total = elements.total;
          this.elementCurrentPage = elements.to - elements.from;
          this.page = elements.current_page;
          this.lastPage = elements.last_page;
          this.links = elements.links;
          window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (error) {
          console.error("Failed to fetch page data:", error);
        }
      }
    },

    async searchElement() {
      try {
        let elements = await this.fetchElementsPaging(
          `${this.$props.url}?search=${this.searchTerm}`
        );
        if (this.$props.typeElement === "hotels") {
          elements = elements.hotels;
        } else {
          elements = elements.restaurants;
        }
        this.elements = elements.data;
        this.total = elements.total;
        this.elementCurrentPage = elements.to - elements.from;
        this.page = elements.current_page;
        this.lastPage = elements.last_page;
        this.links = elements.links;
      } catch (error) {
        console.error("Failed to fetch page data:", error);
      }
    },

    async loadBannerData() {
      const data = await this.fetchBanners();
      let arrayBanners = Object.values(data);
      this.imageListBanner1 = arrayBanners[0] || [];
      this.imageListBanner2 = arrayBanners[1] || [];
      this.imageListBanner3 = arrayBanners[2] || [];
    },

    async loadActivities() {
      const activitiesList = await this.fetchActivity(this.idDestination);
      this.activitiesList = activitiesList.splice(0, 6);
    },

    async loadElements() {
      try {
        let elements = null;
        if (this.typeElement === "hotels") {
          elements = await this.fetchHotelsByPage(this.page);
                  this.elements = elements.data;
        this.elements.sort((a, b) => a.starting_price - b.starting_price);
        this.total = elements.total;
        this.elementCurrentPage = elements.to - elements.from;
        this.page = elements.current_page;
        this.lastPage = elements.last_page;
        this.links = elements.links;
        }
        else if(this.typeElement==="annuaire"){
            try {
        const response = await fetch(`${process.env.VUE_APP_BACKEND_URL}allagencies`)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
                const data = await response.json()
                  this.elements = data;
        this.total = data.length;
                this.elementCurrentPage = 1;
        this.page = 1;
        this.lastPage = elements.last_page;


            }
            catch(error1){
                      console.error('Error fetching agencies:', error1)

            }
        }
        else {
          elements = await this.fetchRestaurantsByPage(this.page);
                  this.elements = elements.data;
        this.elements.sort((a, b) => a.starting_price - b.starting_price);
        this.total = elements.total;
        this.elementCurrentPage = elements.to - elements.from;
        this.page = elements.current_page;
        this.lastPage = elements.last_page;
        this.links = elements.links;
        }

      } catch (error) {
        console.error("Error loading elements: ", error);
      }
    },

    async submitForm() {
      try {
        let urlDestination = "";
        if (this.form.destination) {
          urlDestination = this.$props.url + `?destination_id=${this.form.destination}`;
        } else {
          urlDestination = this.$props.url + `?page=1`;
        }
        let elements = null;
        elements = await this.fetchElementsPaging(urlDestination);
        if (this.$props.typeElement === "hotels") {
          elements = elements.hotels;
        }
        if (this.$props.typeElement === "restaurants") {
          elements = elements.restaurants;
        }
        this.elements = elements.data;
        this.total = elements.total;
        this.elementCurrentPage = elements.to - elements.from;
        this.page = elements.current_page;
        this.lastPage = elements.last_page;
        this.links = elements.links;
      } catch (error) {
        console.error("Failed to fetch page data:", error);
      }
    },

    async loadArticles() {
      try {
        const articlesList = await this.fetchArticles();
        this.articles = articlesList;
      } catch (error) {
        console.error("Error loading articles: ", error);
      }
    },

    async loadDestinations() {
      try {
        const destinationsList = await this.fetchDestinations();
        this.destinations = destinationsList;
      } catch (error) {
        console.error("Error loading destinations: ", error);
      }
    },

    async loadData() {
      try {
        await Promise.all([
          this.loadElements(),
          this.loadBannerData(),
          this.loadActivities(),
          this.loadArticles(),
          this.loadDestinations(),
        ]);
      } catch (error) {
        console.error("Error loading data: ", error);
      }
    },
  },

  async created() {
    this.$store.dispatch("setLoading", true);
    try {
      await this.loadData();
    } catch (error) {
      console.log("error ", error);
    } finally {
      this.$store.dispatch("setLoading", false);
    }
  },
};
</script>

<style scoped>
/* Custom scrollbar for mobile modal */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Focus styles for better accessibility */
button:focus-visible,
input:focus-visible,
select:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Better touch targets on mobile */
@media (max-width: 640px) {
  button {
    min-height: 44px;
  }
  
  input, select {
    min-height: 44px;
  }
}
</style>
